import { ContactCustomerLink, ContactCustomerSummary, ContactModel, CUSTOMER_TYPES } from '@/typings/models/Contact';
import { LeadModel } from '@/typings/models/Lead';
import { INTL_ENTITY } from '@/utils/intl';

/** The contact entity for `<F entity action>` and `getIntlTranslation`. */
export const CONTACT_ENTITY = INTL_ENTITY.CONTACT;

/** Contact tags shown in the project's customer card before the rest folds into "+n". */
export const MAX_VISIBLE_CONTACT_TAGS = 5;

const clean = (value?: string | null) => (value || '').trim();

export const getContactName = (contact?: Partial<ContactModel> | null): string => {
  if (!contact) return '';
  const name =
    clean(contact.fullname) || [clean(contact.firstname), clean(contact.lastname)].filter(Boolean).join(' ');
  return name || clean(contact.email) || clean(contact.phone);
};

/** The customers a contact is linked to, as far as known (list rows carry them all). */
export const getContactCustomers = (contact?: Partial<ContactModel> | null): ContactCustomerLink[] =>
  contact?.customers || [];

/** The contact's link to this customer, else its first one. */
export const getContactCustomer = (
  contact?: Partial<ContactModel> | null,
  customerId?: string | null,
): ContactCustomerLink | undefined => {
  const customers = getContactCustomers(contact);
  return (customerId && customers.find((customer) => customer.id === customerId)) || customers[0];
};

/** The customer a new project for this contact goes to: one it is the primary of, else its first (as the backend dedups). */
export const getContactHomeCustomer = (contact?: Partial<ContactModel> | null): ContactCustomerLink | undefined => {
  const customers = getContactCustomers(contact);
  return customers.find((customer) => customer.is_primary) || customers[0];
};

/** The customer in context: set client-side, else the first one a list row is linked to. */
export const getContactCustomerId = (contact?: Partial<ContactModel> | null): string | undefined =>
  contact?.customer_id || getContactCustomers(contact)[0]?.id || undefined;

/** Already linked to this customer (and so on all its projects). */
export const isContactOfCustomer = (contact: Partial<ContactModel> | null | undefined, customerId?: string | null) =>
  !!customerId &&
  (contact?.customer_id === customerId || getContactCustomers(contact).some((customer) => customer.id === customerId));

/** The company name of an organization customer; '' for a person. */
export const getCustomerCompany = (customer?: Partial<ContactCustomerSummary> | null): string =>
  customer?.type === CUSTOMER_TYPES.ORGANIZATION ? clean(customer.company_name) : '';

/** A customer as people name it: the company, else the person. */
export const getCustomerLabel = (customer?: Partial<ContactCustomerSummary> | null): string => {
  if (!customer) return '';
  return (
    getCustomerCompany(customer) ||
    [clean(customer.firstname), clean(customer.lastname)].filter(Boolean).join(' ') ||
    clean(customer.email)
  );
};

/** A private customer becomes a household once a second contact joins; until then the contact *is* the customer. */
export const isHousehold = (
  customer: Partial<ContactCustomerSummary> | null | undefined,
  contacts: unknown[] = [],
): boolean => customer?.type !== CUSTOMER_TYPES.ORGANIZATION && contacts.length > 1;

/** The household's family names, "Müller/Wagner": distinct last names in contact order, else first names. */
export const getHouseholdNames = (contacts: Partial<ContactModel>[] = []): string => {
  const seen = new Set<string>();
  return contacts
    .map((contact) => clean(contact.lastname) || getContactShortName(contact))
    .filter((name) => {
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join('/');
};

/** A household's name: the one saved on the customer (`company_name`), else "Household Müller/Wagner". */
export const getHouseholdLabel = (
  intl: { formatMessage: (descriptor: { id: string; defaultMessage: string }, values?: Record<string, string>) => string },
  customer: Partial<ContactCustomerSummary> | null | undefined,
  contacts: Partial<ContactModel>[] = [],
): string =>
  clean(customer?.company_name) ||
  intl.formatMessage(
    { id: 'component.contacts.householdName', defaultMessage: 'Household {names}' },
    { names: getHouseholdNames(contacts) },
  );

/**
 * An appointment's contact where naming them adds something next to the
 * project: always for a company, for a private customer only when it's
 * someone other than the customer themself.
 */
export const getAppointmentContactToShow = <T extends Partial<ContactModel>>(
  appointment?: { contact?: T | null; lead?: Pick<LeadModel, 'customer'> | null } | null,
): T | null => {
  const contact = appointment?.contact;
  const name = getContactName(contact);
  if (!contact || !name) return null;
  const customer = appointment?.lead?.customer as
    | (Partial<ContactCustomerSummary> & { primary_contact?: { id?: string } | null })
    | null
    | undefined;
  if (customer?.type === CUSTOMER_TYPES.ORGANIZATION) return contact;
  // The customer's own contact is known by id when the response carries it; names are the fallback.
  const ownContactId = customer?.primary_contact?.id;
  if (ownContactId && contact.id) return contact.id === ownContactId ? null : contact;
  return name === getCustomerLabel(customer) ? null : contact;
};

/**
 * The project's own primary contact when it isn't the customer's. The customer
 * fields (`customer.email`, …) belong to the customer's primary contact, so
 * project lists show this one instead, read-only.
 */
export const getSeparateProjectContact = (
  lead?: Pick<LeadModel, 'primary_contact'> | null,
): ContactModel | null => (lead?.primary_contact && !lead.primary_contact.is_primary ? lead.primary_contact : null);

/** The company a contact works for: the first organization it's linked to. */
export const getContactCompany = (contact?: Partial<ContactModel> | null): string =>
  getContactCustomers(contact).map(getCustomerCompany).find(Boolean) || '';

/** Short label for a tag: the first name, else whatever names the contact. */
export const getContactShortName = (contact?: Partial<ContactModel> | null): string =>
  clean(contact?.firstname) || getContactName(contact);

export const getContactInitials = (contact?: Partial<ContactModel> | null): string => {
  const first = clean(contact?.firstname);
  const last = clean(contact?.lastname);
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
  const name = getContactName(contact);
  if (!name) return '?';
  const words = name.split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : name.slice(0, 2)).toUpperCase();
};

/** Stable avatar tone per contact, so a person keeps their colour everywhere. */
export const isLeadPrimaryContact = (
  contact: Pick<ContactModel, 'id'> | null | undefined,
  lead: Pick<LeadModel, 'primary_contact_id'> | null | undefined,
): boolean => !!contact && !!lead?.primary_contact_id && contact.id === lead.primary_contact_id;

/**
 * Splits the tag bar into what is shown and what folds into "+n". The selected
 * contact always stays visible, taking the last visible slot when needed.
 */
export const splitVisibleContacts = (
  contacts: ContactModel[],
  selectedId?: string | null,
  max: number = MAX_VISIBLE_CONTACT_TAGS,
): { visible: ContactModel[]; overflow: ContactModel[] } => {
  if (contacts.length <= max) return { visible: contacts, overflow: [] };
  const visible = contacts.slice(0, max);
  const selectedIndex = contacts.findIndex((contact) => contact.id === selectedId);
  if (selectedIndex >= max) {
    visible[max - 1] = contacts[selectedIndex];
  }
  const visibleIds = new Set(visible.map((contact) => contact.id));
  return { visible, overflow: contacts.filter((contact) => !visibleIds.has(contact.id)) };
};

/** Phone digits without leading zeros, so "0664 22…" can be found inside "+43 664 22…". */
const phoneDigits = (value?: string | null) => (value || '').replace(/\D/g, '').replace(/^0+/, '');

/** Same number written with or without the country code: one ends with the other. */
const samePhone = (a?: string | null, b?: string | null) => {
  const [short, long] = [phoneDigits(a), phoneDigits(b)].sort((x, y) => x.length - y.length);
  return short.length >= 6 && long.endsWith(short);
};

export const contactMatchesSearch = (contact: ContactModel, term: string): boolean => {
  const query = term.trim().toLowerCase();
  if (!query) return true;
  const links = getContactCustomers(contact);
  const haystack = [
    getContactName(contact),
    contact.email,
    contact.role,
    ...links.map((customer) => customer.role),
    ...links.map(getCustomerCompany),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (query.split(/\s+/).every((word) => haystack.includes(word))) return true;
  const queryDigits = phoneDigits(query);
  return /^[+\d\s/()-]+$/.test(query) && queryDigits.length >= 3 && phoneDigits(contact.phone).includes(queryDigits);
};

const normalizeEmail = (value?: string | null) => clean(value).toLowerCase();

/** An existing contact that already uses this email or phone, for the duplicate warning. */
export const findDuplicateContact = (
  contacts: ContactModel[],
  { email, phone }: { email?: string | null; phone?: string | null },
  excludeId?: string,
): { contact: ContactModel; field: 'email' | 'phone' } | undefined => {
  const candidates = contacts.filter((contact) => contact.id !== excludeId);
  const wantedEmail = normalizeEmail(email);
  if (wantedEmail) {
    const match = candidates.find((contact) => normalizeEmail(contact.email) === wantedEmail);
    if (match) return { contact: match, field: 'email' };
  }
  if (phone) {
    const match = candidates.find((contact) => samePhone(contact.phone, phone));
    if (match) return { contact: match, field: 'phone' };
  }
  return undefined;
};

/**
 * Seeds the new-contact form from what was typed into the search: an email,
 * a phone number, or "First Last".
 */
export const prefillFromSearch = (term: string): Partial<ContactModel> => {
  const value = term.trim();
  if (!value) return {};
  if (value.includes('@')) return { email: value };
  if (/^[+\d][\d\s/()-]{3,}$/.test(value)) return { phone: value };
  const [firstname, ...rest] = value.split(/\s+/);
  const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
  return { firstname: capitalize(firstname), lastname: rest.map(capitalize).join(' ') || undefined };
};

/** Distinct roles already in use, most frequent first — fallback for role suggestions. */
export const collectKnownRoles = (contacts: ContactModel[]): string[] => {
  const counts = new Map<string, { role: string; count: number }>();
  const count = (value?: string | null) => {
    const role = clean(value);
    if (!role) return;
    const key = role.toLowerCase();
    const entry = counts.get(key);
    counts.set(key, { role: entry?.role || role, count: (entry?.count || 0) + 1 });
  };
  contacts.forEach((contact) => {
    // List rows carry the role per customer; customer and project rows carry the one in context.
    const links = getContactCustomers(contact);
    if (links.length) links.forEach((customer) => count(customer.role));
    else count(contact.role);
  });
  return [...counts.values()].sort((a, b) => b.count - a.count).map((entry) => entry.role);
};

/** A role as one trimmed line, within the backend's 255 characters. */
export const ROLE_MAX_LENGTH = 255;

/** A role list as saved: one line each, no blanks, no repeats (ignoring case), in the given order. */
export const normalizeRoleList = (roles: string[]): string[] => {
  const seen = new Set<string>();
  return roles
    .map((role) => clean(role).replace(/\s+/g, ' ').slice(0, ROLE_MAX_LENGTH))
    .filter((role) => {
      const key = role.toLowerCase();
      if (!role || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const filterRoleSuggestions = (roles: string[], term: string, limit = 8): string[] => {
  const query = clean(term).toLowerCase();
  const seen = new Set<string>();
  return roles
    .filter((role) => {
      const key = role.toLowerCase();
      if (seen.has(key) || key === query) return false;
      seen.add(key);
      return !query || key.includes(query);
    })
    .slice(0, limit);
};

/**
 * The customer's other projects. Linked leads also match by email or phone, so
 * leads of other customers are left out.
 */
export const getOtherLeadsOfCustomer = <T extends Pick<LeadModel, 'id' | 'customer'>>(
  customerId: string | undefined,
  linkedLeads: T[] = [],
  currentLeadId?: string,
): T[] => {
  if (!customerId) return [];
  return linkedLeads.filter((lead) => lead.id !== currentLeadId && lead.customer?.id === customerId);
};

const MIRRORED_CUSTOMER_FIELDS =['firstname', 'lastname', 'email', 'phone', 'gender', 'locale'] as const;

/**
 * The project with a fresh contact list from a lead-contacts response. The
 * list comes lead-primary first, so without an explicit id the first one is
 * the project's primary.
 */
export const applyLeadContacts = <T extends Partial<LeadModel>>(
  lead: T,
  contacts: ContactModel[],
  primaryContactId?: string | null,
): T => {
  const primaryId =
    [primaryContactId, lead.primary_contact_id].find((id) => id && contacts.some((c) => c.id === id)) ||
    contacts[0]?.id ||
    null;
  return {
    ...lead,
    contacts,
    primary_contact_id: primaryId,
    primary_contact: contacts.find((contact) => contact.id === primaryId) || null,
  };
};

/**
 * The project after one of its contacts was saved. The customer columns are a
 * copy of the customer's primary contact, so those follow too.
 */
export const replaceLeadContact = <T extends Partial<LeadModel>>(lead: T, contact: ContactModel): T => {
  const contacts = (lead.contacts || []).map((item) => (item.id === contact.id ? { ...item, ...contact } : item));
  const saved = contacts.find((item) => item.id === contact.id);
  const customer =
    saved?.is_primary && lead.customer
      ? {
          ...lead.customer,
          ...Object.fromEntries(MIRRORED_CUSTOMER_FIELDS.map((field) => [field, saved[field] ?? undefined])),
        }
      : lead.customer;
  return {
    ...lead,
    contacts,
    customer,
    primary_contact:
      saved && (lead.primary_contact_id === contact.id || lead.primary_contact?.id === contact.id)
        ? saved
        : lead.primary_contact,
  };
};

/** `mergeWith` customizer: a contact list is replaced whole, never merged by index. */
export const replaceContactArrays = (_target: unknown, source: unknown, key: string) =>
  key === 'contacts' && Array.isArray(source) ? source : undefined;

/**
 * Rows of a contact list response. Laravel's paginated envelope is
 * `{data: [...], links, total, …}`; a `{data: {items|data: [...]}}` wrapper is tolerated.
 */
export const extractContactList = (
  body: any,
): { items: ContactModel[]; links?: any; total?: number; total_pages?: number } => {
  if (Array.isArray(body)) return { items: body };
  const page = body?.data && !Array.isArray(body.data) ? body.data : body;
  const items = Array.isArray(page?.items) ? page.items : Array.isArray(page?.data) ? page.data : [];
  return { items, links: page?.links, total: page?.total, total_pages: page?.total_pages };
};

/** Minimum typed length before looking a company up (the customer search needs 3). */
export const MIN_COMPANY_SEARCH_LENGTH = 3;

/**
 * The existing company the typed name most likely means: an exact name first,
 * else one where either name starts with the other ("Hotel Seeblick" ↔
 * "Hotel Seeblick GmbH"). The search also matches contact names, so hits
 * without a matching company name are ignored.
 */
export const findMatchingCompany = <T extends { company_name?: string | null }>(
  customers: T[] = [],
  typed?: string | null,
): T | undefined => {
  const name = clean(typed).toLowerCase();
  if (name.length < MIN_COMPANY_SEARCH_LENGTH) return undefined;
  const companyOf = (customer: T) => clean(customer.company_name).toLowerCase();
  return (
    customers.find((customer) => companyOf(customer) === name) ||
    customers.find((customer) => {
      const company = companyOf(customer);
      return !!company && (company.startsWith(name) || name.startsWith(company));
    })
  );
};

/**
 * A term that finds the contact's projects in the project search, which
 * matches the start of any contact's email or name: the most specific one.
 */
export const contactProjectSearchTerm = (contact?: Partial<ContactModel> | null): string | undefined =>
  [contact?.email, getContactCustomers(contact)[0]?.email, getContactName(contact)]
    .map(clean)
    .find((term) => term.length >= 3) || undefined;

/** The projects (from a project search) this contact is on, the project's primary flagged. */
export const projectsOfContact = <T extends Pick<LeadModel, 'id' | 'contacts' | 'primary_contact_id'>>(
  contactId: string,
  leads: T[] = [],
): T[] => leads.filter((lead) => lead.contacts?.some((contact) => contact.id === contactId));
